import React, { useState, useMemo } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Link,
    Box,
    Chip,
    TextField,
    InputAdornment,
    Paper,
    Divider,
    Badge
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsIcon from "@mui/icons-material/Settings";
import GroupIcon from "@mui/icons-material/Group";
import BuyMeCoffeeButton from "../components/BuyMeCoffeeButton";
import BrandsInfo from "../components/BrandsInfo";
import '../style/Faq.css';

const FAQPage = () => {
    // State for selected categories and search
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedAccordion, setExpandedAccordion] = useState(null);

    const faqs = [
        {
            question: "Where do the data come from? How accurate is it?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        Our product information comes from multiple sources. Some data, like ingredients or basic nutritional values, is directly provided by manufacturers through their official product specifications.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        For other information like category, texture, and health benefits, we gather data from various sources including product labels, manufacturer PDFs, and product descriptions. In some cases, we may infer certain details from the product description or packaging information when not explicitly stated by the manufacturer.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        We do our best to keep everything accurate and up-to-date, but we always recommend double-checking with the actual product packaging and consulting your veterinarian for your cat's specific needs.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        If you notice any inaccuracies in our data, you can help us improve by using the "Flag Issue" button on any product page. The Picky Kitty takes all feedback seriously and will investigate and correct any reported issues.
                    </Typography>
                </>
            ),
            category: "data",
            tags: ["data", "accuracy", "sources"]
        },
        {
            question: "Why does The Picky Kitty show that a product doesn't meet AAFCO requirements or contains grain when the packaging indicates otherwise?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        Discrepancies between our data and product packaging can occur for several reasons:
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        • <strong>Formula changes:</strong> Products may have been reformulated since we last updated our database
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        • <strong>Regional variations:</strong> The same product might have different formulations in different regions
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        • <strong>Outdated sources:</strong> Information we gathered may be from an older version of the product
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        • <strong>Processing errors:</strong> Mistakes can occur during data entry or processing
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        In particular:
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        • <strong>AAFCO communication:</strong> Not all manufacturers communicate whether their products meet AAFCO requirements on their websites. When no AAFCO information is found, the product will show as "Not meeting requirements" on our website even if it may actually meet them.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        • <strong>Ingredient analysis:</strong> Our grain-free classification is based on information found in the product name or description as well as ingredient analysis. Some grain-containing ingredients (like certain starches) might not be immediately obvious.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        If you notice such a discrepancy, please use the "Flag Issue" button on the product page or email us at{" "}
                        <Link href="mailto:meow@thepickykitty.com">
                            meow@thepickykitty.com
                        </Link>{" "}
                        with photos of the current packaging so we can investigate and update our data.
                    </Typography>
                </>
            ),
            category: "data",
            tags: ["AAFCO", "grain", "packaging", "discrepancies", "ingredients", "errors", "accuracy", "sources", "manufacturers"]
        },
        {
            question: "What should I do if I find incorrect data on the site?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        If you notice any incorrect information on our site, we want to know about it! You can report issues in several ways:
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        • Use the "Flag Issue" button on any product page to report specific problems with that product's data
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        • Email us directly at{" "}
                        <Link href="mailto:meow@thepickykitty.com">
                            meow@thepickykitty.com
                        </Link>{" "}
                        with details about what you found
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        • Fill out our{" "}
                        <Link href="https://forms.gle/DmVsEtRtiMitEAMt5" target="_blank" rel="noopener noreferrer">Feedback form</Link>{" "}
                        to let us know about any issues
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        When reporting incorrect data, please include as much detail as possible, such as the product name, what information is wrong, and what the correct information should be. This helps us fix issues quickly and accurately.
                    </Typography>
                </>
            ),
            category: "data",
            tags: ["incorrect", "errors", "reporting", "feedback"]
        },
        {
            question: "Do you show nutritional values on a dry matter basis or as-fed?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        We display both dry matter basis (DMB) and as-fed values where available. As-fed values show nutrients exactly as they appear on the label, while dry matter basis removes the moisture content for a more accurate comparison between wet and dry foods.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        For example, a wet food might show 10% protein as-fed, but 50% on a dry matter basis after removing the ~80% moisture content. This makes it easier to compare to dry food, which might show 30% protein as-fed and 32% on a dry matter basis (since dry food typically has very low moisture content).
                    </Typography>
                </>
            ),
            category: "data",
            tags: ["nutrition", "dry matter", "comparison"]
        },
        {
            question: "How can I filter cat foods on the site?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        You can filter cat foods by broader categories like protein type, food category (dry, wet, treats, etc.), format (can, pouch, bag, etc.), specific  nutrional features (grain free, limited ingredient, etc.), nutriitional content (protein, fat, fiber, etc.), and more.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        We're working on adding even more detailed options in the future based on user feedback!
                    </Typography>
                </>
            ),
            category: "features",
            tags: ["filtering", "search", "categories"]
        },
        {
            question: "Do you have recommendations for cats with specific health conditions?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        While we provide detailed nutritional data that you can use for filtering, we don't offer medical advice or specific health recommendations.
                        It's always best to discuss your cat's dietary needs with your veterinarian.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        We do display a "Health benefits" field for each product, based on information provided directly by the manufacturers.
                        However, please note that we are not qualified to assess or guarantee the suitability of any food for specific medical conditions.
                    </Typography>
                </>
            ),
            category: "health",
            tags: ["health", "medical", "veterinarian"]
        },
        {
            question: "How often is the cat food database updated?",
            answer: "We update the database monthly with new products and refreshed nutritional information. If a manufacturer changes a formula, we do our best to update it as soon as possible.",
            category: "data",
            tags: ["updates", "database", "frequency"]
        },
        {
            question: "I can't find the brand I'm interested in. Will new brands or products be added in the future?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        We are continuously working on adding new brands and products to our database.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        If there's a specific brand you'd like to see, please email us at{" "}
                        <Link href="mailto:meow@thepickykitty.com">
                            meow@thepickykitty.com
                        </Link>{" "}
                        or fill out the <Link href="https://forms.gle/DmVsEtRtiMitEAMt5" target="_blank" rel="noopener noreferrer">Feedback form</Link> and 
                        we'll do our best to include it in the next update!
                    </Typography>
                </>
            ),
            category: "data",
            tags: ["brands", "products", "requests"]
        },
        {
            question: "Can I contribute information about cat food products?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        Yes! If you notice missing information or know about a new product, we'd love to hear from you.
                        You can email us directly at{" "}
                        <Link href="mailto:meow@thepickykitty.com">
                            meow@thepickykitty.com
                        </Link>.
                        We review every message carefully!
                    </Typography>
                </>
            ),
            category: "community",
            tags: ["contribute", "feedback", "community"]
        },
        {
            question: "Is there a mobile app for The Picky Kitty?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        We don't have a mobile app at the moment.
                        However, The Picky Kitty website is fully responsive and should work smoothly on most phones and tablets.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        If you think a mobile app would make your experience even better, we'd love to hear your feedback!
                        Feel free to email us at{" "}
                        <Link href="mailto:meow@thepickykitty.com">
                            meow@thepickykitty.com
                        </Link> or fill out the <Link href="https://forms.gle/DmVsEtRtiMitEAMt5" target="_blank" rel="noopener noreferrer">Feedback form</Link>.
                    </Typography>
                </>
            ),
            category: "features",
            tags: ["mobile", "app", "responsive"]
        },
        {
            question: "Do you rate or rank cat foods?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        No, we don't rate or rank cat foods overall.
                        Our goal is to provide clear, unbiased data so you can make informed decisions based on your cat's unique needs and preferences.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        In the Recommendations tool, we do display a ranked list, however this ranking is based purely on how well a food matches the information you provided
                        (such as preferred protein types, texture, and products/brands your cat likes or dislikes).
                        It is not based on any judgment or rating of food quality.
                    </Typography>
                </>
            ),
            category: "features",
            tags: ["ratings", "rankings", "recommendations"]
        },
        {
            question: "How are the recommendations generated?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        Our recommendations are purely based on your preferences and the information you provide about your kitty likes and dislikes. We do not rate products or brands: our system simply matches your criteria to available products in our database.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        There is a small element of randomness built into our recommendation algorithm to encourage exploration. This means you might occasionally see products that don't perfectly match all your specified criteria, but still meet most of them. This helps you discover new options you might not have considered otherwise.
                    </Typography>
                </>
            ),
            category: "features",
            tags: ["algorithm", "preferences", "matching"]
        },
        {
            question: "How can we support The Picky Kitty?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        The best way to support The Picky Kitty is by sharing the site with other cat lovers, sending us feedback, or making a small donation through our{" "}
                        <Link href="https://www.buymeacoffee.com/thepickykitty" target="_blank" rel="noopener noreferrer">
                            Buy Me a Coffee
                        </Link>{" "}
                        page. Every little purr of encouragement helps!
                    </Typography>
                    <div className="buy-me-coffee-container">
                        <BuyMeCoffeeButton />
                    </div>
                    <Typography className="faq-answer" paragraph>
                        You can also help us by making Amazon purchases through our website. We get a small commission for every purchase made through our affiliate links, which helps keep the site running. For more information about our affiliate relationships, please see our{" "}
                        <Link href="/disclosure" target="_blank" rel="noopener noreferrer">
                            disclosure page
                        </Link>.
                    </Typography>
                </>
            ),
            category: "community",
            tags: ["support", "donations", "affiliate"]
        },
        {
            question: "Which regions or countries does The Picky Kitty cover?",
            answer: (
                <>
                    <Typography className="faq-answer" paragraph>
                        Currently, The Picky Kitty primarily focuses on products available in the United States, though some of these products may also be available in other countries.
                    </Typography>
                    <Typography className="faq-answer" paragraph>
                        However, The Picky Kitty loves travelling and dreams big, so it might expand to other regions in the future if it helps picky kitties find food they love. If you like what we are doing, please let us know by emailing us at{" "}
                        <Link href="mailto:meow@thepickykitty.com">
                            meow@thepickykitty.com
                        </Link>.
                    </Typography>
                </>
            ),
            category: "data",
            tags: ["regions", "countries", "availability"]
        },
        {
            question: "What brands are currently available in the database?",
            answer: <BrandsInfo />,
            category: "data",
            tags: ["brands", "database", "products", "availability"]
        }
    ];

    // Get unique categories with counts
    const categories = useMemo(() => {
        const categoryCounts = faqs.reduce((acc, faq) => {
            acc[faq.category] = (acc[faq.category] || 0) + 1;
            return acc;
        }, {});
        
        return Object.keys(categoryCounts).map(category => ({
            name: category,
            count: categoryCounts[category]
        }));
    }, []);

    // Filter FAQs by category and search
    const filteredFaqs = useMemo(() => {
        let filtered = faqs;
        
        // Filter by categories
        if (selectedCategories.length > 0) {
            filtered = filtered.filter(faq => selectedCategories.includes(faq.category));
        }
        
        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(faq => 
                faq.question.toLowerCase().includes(query) ||
                (faq.tags && faq.tags.some(tag => tag.toLowerCase().includes(query))) ||
                (typeof faq.answer === 'string' && faq.answer.toLowerCase().includes(query))
            );
        }
        
        return filtered;
    }, [selectedCategories, searchQuery]);

    // Handle category chip click
    const handleCategoryClick = (category) => {
        setSelectedCategories(prev => {
            if (prev.includes(category)) {
                // Remove category if already selected
                return prev.filter(cat => cat !== category);
            } else {
                // Add category if not selected
                return [...prev, category];
            }
        });
        setExpandedAccordion(null); // Reset expanded accordion when filtering
    };

    // Handle accordion expansion
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedAccordion(isExpanded ? panel : null);
    };

    // Clear all filters
    const clearFilters = () => {
        setSelectedCategories([]);
        setSearchQuery("");
        setExpandedAccordion(null);
    };

    return (
        <div className="faq-container">
            <div className="page-header">
                <h1 className="page-title">Frequently Asked Questions</h1>
                <img
                    src="/picky-kitty/faq.png"
                    alt="The Picky Kitty FAQ"
                    className="header-image"
                />
            </div>

            <div className="faq-content">
                {/* Search and Filter Section */}
                <Paper elevation={2} className="search-filter-section">
                    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
                        {/* Search Bar */}
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search questions, topics, or keywords..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: { xs: 1, md: 1.5 } }}
                        />

                        {/* Category Filters */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.25, sm: 2.5, md: 2 }, mt: { sm: 2 } }}>
                            <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Filter by Category
                            </Typography>
                        </Box>

                        <Box className="category-chips" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: { xs: 0.25, md: 2 } }}>
                            {categories.map(({ name, count }) => (
                                <Chip
                                    key={name}
                                    label={name === 'health' ? 'Medical Conditions' : name.charAt(0).toUpperCase() + name.slice(1)}
                                    icon={name === 'health' ? <LocalHospitalIcon /> : name === 'data' ? <StorageIcon /> : name === 'features' ? <SettingsIcon /> : name === 'community' ? <GroupIcon /> : null}
                                    onClick={() => handleCategoryClick(name)}
                                    color={selectedCategories.includes(name) ? "primary" : "default"}
                                    variant={selectedCategories.includes(name) ? "filled" : "outlined"}
                                    className="category-chip"
                                />
                            ))}
                        </Box>

                        {/* Clear Filters Button */}
                        {(selectedCategories.length > 0 || searchQuery) && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 0.5, sm: 2, md: 2 } }}>
                                <Chip
                                    label="Clear All Filters"
                                    onClick={clearFilters}
                                    variant="filled"
                                    sx={{ 
                                        cursor: 'pointer',
                                        px: { xs: 1.5, sm: 2.5, md: 2 },
                                        py: { xs: 0.5, sm: 1.5, md: 1 },
                                        fontSize: '0.9rem',
                                        fontWeight: 500,
                                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                        borderColor: 'rgba(59, 130, 246, 0.3)',
                                        color: '#2563eb',
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                            borderColor: 'rgba(59, 130, 246, 0.5)',
                                            color: '#1d4ed8',
                                            backgroundColor: 'rgba(59, 130, 246, 0.15)'
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* Results Summary */}
                {filteredFaqs.length > 0 && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {filteredFaqs.length} of {faqs.length} questions
                            {selectedCategories.length > 0 && ` in ${selectedCategories.length === 1 ? `"${selectedCategories[0]}"` : `"${selectedCategories.join('", "')}"`} ${selectedCategories.length === 1 ? 'category' : 'categories'}`}
                            {searchQuery && ` matching "${searchQuery}"`}
                        </Typography>
                    </Box>
                )}

                {/* FAQ Accordions */}
                {filteredFaqs.length > 0 ? (
                    filteredFaqs.map((faq, index) => (
                        <Accordion 
                            key={index} 
                            className="faq-accordion"
                            expanded={expandedAccordion === `panel${index}`}
                            onChange={handleAccordionChange(`panel${index}`)}
                        >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel${index}-content`}
                            id={`panel${index}-header`}
                        >
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    width: '100%',
                                    gap: 2,
                                    pr: 1
                                }}>
                                    <Typography className="faq-question" sx={{ flex: 1 }}>
                                        {faq.question}
                                    </Typography>
                                    <Chip 
                                        label={faq.category === 'health' ? 'Medical' : 
                                               faq.category === 'data' ? 'Data' :
                                               faq.category === 'features' ? 'Features' :
                                               faq.category === 'community' ? 'Community' :
                                               faq.category.charAt(0).toUpperCase() + faq.category.slice(1)} 
                                        size="small" 
                                        variant="outlined"
                                        icon={faq.category === 'health' ? <LocalHospitalIcon sx={{ fontSize: '0.875rem' }} /> : 
                                              faq.category === 'data' ? <StorageIcon sx={{ fontSize: '0.875rem' }} /> : 
                                              faq.category === 'features' ? <SettingsIcon sx={{ fontSize: '0.875rem' }} /> : 
                                              faq.category === 'community' ? <GroupIcon sx={{ fontSize: '0.875rem' }} /> : 
                                              null}
                                        sx={{ 
                                            ml: 'auto',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            height: '24px',
                                            minWidth: '60px',
                                            backgroundColor: 'rgba(107, 114, 128, 0.1)',
                                            borderColor: 'rgba(107, 114, 128, 0.3)',
                                            color: '#374151',
                                            '& .MuiChip-icon': {
                                                color: 'inherit',
                                                opacity: 0.8
                                            },
                                            '&:hover': {
                                                backgroundColor: 'rgba(107, 114, 128, 0.15)',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                            },
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            cursor: 'default'
                                        }}
                                    />
                                </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {typeof faq.answer === "string" ? (
                                <Typography className="faq-answer">{faq.answer}</Typography>
                            ) : (
                                faq.answer
                            )}
                        </AccordionDetails>
                    </Accordion>
                    ))
                ) : (
                    <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No questions found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Try adjusting your search terms or filters
                        </Typography>
                    </Paper>
                )}
            </div>
        </div>
    );
};

export default FAQPage;